// Typed client for the `restoration-generate` Supabase edge function.
//
// This is the structured-output companion to the chat-style `mentor-chat`
// endpoint. Each task returns a strongly-typed payload the UI can drop into
// the appropriate surface (conversation guide, reflection prompts, removal
// decision card, parent email, etc.).
//
// Usage:
//   const res = await generate("conversation_guide", { incident }, token);
//   if ("markdown" in res) { ... }

import { supabase } from "@/lib/supabase";
import type { Incident } from "./types";

// ── Task + result shapes ─────────────────────────────────────────────────
// Must stay in sync with supabase/functions/restoration-generate/tasks.ts.

export type RestorationTask =
  | "conversation_guide"
  | "reflection_prompts"
  | "motivation"
  | "removal_decision"
  | "reentry_plan"
  | "post_convo_debrief"
  | "parent_email"
  | "parent_phone_script"
  | "parent_student_call"
  | "incident_polish"
  | "missing_checks"
  | "doc_narrative";

export type ReflectionPromptsResult = {
  intro: string;
  prompts: { question: string; hint: string }[];
  closing: string;
};

export type MotivationResult = {
  hypotheses: { label: string; why: string; probe: string }[];
};

export type RemovalDecisionResult = {
  recommendation: "stay_in_class" | "office_redirect" | "iss_or_formal_removal";
  headline: string;
  rationale: string;
  considerations: string[];
  flags: string[];
};

export type ReentryPlanResult = {
  work_plan: string;
  checkin_schedule: string;
  reentry_script: string;
  supports: string[];
};

export type DebriefResult = {
  what_worked: string[];
  watch_for: string[];
  follow_up_steps: { when: string; action: string }[];
};

export type ParentEmailResult = { subject: string; body: string };
export type ParentScriptResult = { script: string };

export type MissingChecksResult = {
  ready: boolean;
  gaps: { field: string; why_it_matters: string; suggested_prompt: string }[];
};

// Union of every possible content shape. Callers discriminate by `kind`.
// We deliberately keep this a flat (non-generic) union because TypeScript
// struggles to narrow a generic-parameterized discriminated union across an
// `ok` literal — callers would have to cast at every error branch. Here a
// plain `if (!res.ok)` narrows correctly.
export type TaskResult =
  | { kind: "markdown"; markdown: string }
  | { kind: "reflection_prompts"; data: ReflectionPromptsResult }
  | { kind: "motivation"; data: MotivationResult }
  | { kind: "removal_decision"; data: RemovalDecisionResult }
  | { kind: "reentry_plan"; data: ReentryPlanResult }
  | { kind: "debrief"; data: DebriefResult }
  | { kind: "parent_email"; data: ParentEmailResult }
  | { kind: "parent_script"; data: ParentScriptResult }
  | { kind: "missing_checks"; data: MissingChecksResult };

// Flat result shape rather than a discriminated union — the app runs with
// strictNullChecks off, so TS can't reliably narrow `res.ok` to pull `error`
// vs `content`. Callers guard with `if (!res.ok) ... res.error` and
// `if (res.ok && res.content?.kind === "...")` which works under either
// strictness setting.
export type GenerateResult = {
  ok: boolean;
  task?: RestorationTask;
  content?: TaskResult;
  error?: string;
};

// ── Incident slim ────────────────────────────────────────────────────────
// Only the fields the function uses. Avoids shipping id/user_id/timestamps
// and keeps the request payload small.

export type IncidentSlim = {
  student_identifier?: string;
  grade_level?: string;
  teacher_name?: string;
  incident_description?: string;
  repeat_incident?: boolean;
  referring_teacher_notes?: string;
  prior_interventions?: string;
  prior_interventions_checked?: string[];
  teacher_requested_outcome?: string;
  teacher_outcome_checked?: string[];
  iep_504_flag?: boolean;
  student_reflection_responses?: string;
  conversation_notes?: string;
  safety_check?: string;
  chosen_path?: string;
  removal_type?: string;
  removal_rationale?: string;
  iss_work_plan?: string;
  return_to_class_plan?: string;
  reentry_conversation_needed?: boolean;
  reentry_facilitator?: string;
  reentry_checkin_schedule?: string;
  parent_call_notes?: string;
  parent_contact_type?: string;
  behavior_follow_up?: string;
  follow_up_date?: string;
  due_process_acknowledged?: boolean;
};

export function slimIncident(inc: Incident): IncidentSlim {
  return {
    student_identifier: inc.student_identifier,
    grade_level: inc.grade_level,
    teacher_name: inc.teacher_name,
    incident_description: inc.incident_description,
    repeat_incident: inc.repeat_incident,
    referring_teacher_notes: inc.referring_teacher_notes,
    prior_interventions: inc.prior_interventions,
    prior_interventions_checked: inc.prior_interventions_checked,
    teacher_requested_outcome: inc.teacher_requested_outcome,
    teacher_outcome_checked: inc.teacher_outcome_checked,
    iep_504_flag: inc.iep_504_flag,
    student_reflection_responses: inc.student_reflection_responses,
    conversation_notes: inc.conversation_notes,
    safety_check: inc.safety_check,
    chosen_path: inc.chosen_path,
    removal_type: inc.removal_type,
    removal_rationale: inc.removal_rationale,
    iss_work_plan: inc.iss_work_plan,
    return_to_class_plan: inc.return_to_class_plan,
    reentry_conversation_needed: inc.reentry_conversation_needed,
    reentry_facilitator: inc.reentry_facilitator,
    reentry_checkin_schedule: inc.reentry_checkin_schedule,
    parent_call_notes: inc.parent_call_notes,
    parent_contact_type: inc.parent_contact_type,
    behavior_follow_up: inc.behavior_follow_up,
    follow_up_date: inc.follow_up_date,
    due_process_acknowledged: inc.due_process_acknowledged,
  };
}

// ── Invoke ───────────────────────────────────────────────────────────────

// `incident` accepts either a full `Incident` (which we slim server-side) or
// a pre-slimmed partial payload for callers that don't have a full incident
// object handy (e.g. shared components that receive individual props).
export type GenerateInput = {
  incident: Incident | IncidentSlim;
  extras?: Record<string, unknown>;
};

export async function generate(
  task: RestorationTask,
  input: GenerateInput,
  accessToken: string | undefined,
): Promise<GenerateResult> {
  if (!accessToken) {
    return { ok: false, error: "You need to be signed in to use AI generations." };
  }

  // If the caller handed us a full `Incident`, slim it here. If it's already
  // an IncidentSlim, pass through as-is.
  const incidentPayload = "id" in (input.incident ?? {})
    ? slimIncident(input.incident as Incident)
    : (input.incident as IncidentSlim);

  const payload = {
    task,
    input: {
      incident: incidentPayload,
      extras: input.extras,
    },
  };

  try {
    const invokeResult = await supabase.functions.invoke("restoration-generate", {
      body: payload,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const { data, error } = invokeResult;
    const rawResponse =
      (invokeResult as unknown as { response?: Response }).response ??
      (error as unknown as { context?: Response })?.context;

    if (error) {
      let detail = error instanceof Error ? error.message : String(error);
      try {
        if (rawResponse && typeof rawResponse.clone === "function") {
          const text = await rawResponse.clone().text();
          if (text) {
            try {
              const parsed = JSON.parse(text);
              if (parsed?.error) detail = String(parsed.error);
              else if (parsed?.message) detail = String(parsed.message);
              else detail = `HTTP ${rawResponse.status}: ${text.slice(0, 200)}`;
            } catch {
              detail = `HTTP ${rawResponse.status}: ${text.slice(0, 200)}`;
            }
          }
        }
      } catch (parseErr) {
        console.warn("[restoration-generate] could not parse error body", parseErr);
      }
      console.error("[restoration-generate] invoke error:", error, "→", detail);
      return { ok: false, error: detail };
    }

    if (data?.error) {
      return { ok: false, error: String(data.error) };
    }
    if (!data?.content) {
      return { ok: false, error: "Empty response from generator." };
    }
    return { ok: true, task, content: data.content as TaskResult };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error("[restoration-generate] error:", err);
    return { ok: false, error: msg };
  }
}
